import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { RefreshCw, ExternalLink, Settings, CheckCircle2, XCircle, HelpCircle, Clock, Rocket } from 'lucide-react';

const STATUS_META = {
  pending:     { icon: Clock,        label: 'Pending',       color: 'text-[#FCD535]', bg: 'bg-[#FCD535]/10' },
  under_review:{ icon: Clock,        label: 'Under Review',  color: 'text-[#3F8EFC]', bg: 'bg-[#3F8EFC]/10' },
  needs_info:  { icon: HelpCircle,   label: 'Needs Info',    color: 'text-[#F0B90B]', bg: 'bg-[#F0B90B]/15' },
  approved:    { icon: CheckCircle2, label: 'Approved',      color: 'text-[#0ECB81]', bg: 'bg-[#0ECB81]/10' },
  rejected:    { icon: XCircle,      label: 'Rejected',      color: 'text-[#F6465D]', bg: 'bg-[#F6465D]/10' },
};

const STATUS_ORDER = ['pending', 'under_review', 'needs_info', 'approved', 'rejected'];

export default function ListingsAdminPanel() {
  const [tab, setTab] = useState('applications');
  const [statusFilter, setStatusFilter] = useState('');
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [detail, setDetail] = useState(null); // full application
  const [newStatus, setNewStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [cfgDraft, setCfgDraft] = useState(null);

  const load = async () => {
    try {
      const q = statusFilter ? `?status=${statusFilter}&limit=500` : '?limit=500';
      const r = await api.get(`/admin/listings${q}`);
      setRows(r.data.applications || []);
      setCounts(r.data.counts || {});
    } catch (e) { toast.error(e.response?.data?.detail || 'Load failed'); }
  };
  const loadCfg = async () => {
    try {
      const r = await api.get('/admin/listings/config');
      setCfg(r.data.config); setCfgDraft(r.data.config);
    } catch (e) { toast.error(e.response?.data?.detail || 'Config load failed'); }
  };

  useEffect(() => { load(); }, [statusFilter]); // eslint-disable-line
  useEffect(() => { if (tab === 'settings' && !cfg) loadCfg(); }, [tab]); // eslint-disable-line

  const openDetail = async (row) => {
    setDetail(row); setNewStatus(row.status); setNotes(row.admin_notes || '');
  };

  const updateStatus = async () => {
    if (!detail) return;
    setBusy(true);
    try {
      const r = await api.put(`/admin/listings/${detail.application_id}/status`, {
        status: newStatus, admin_notes: notes,
      });
      const draftMsg = r.data.launchpad_draft_id ? ` · draft ${r.data.launchpad_draft_id} created` : '';
      toast.success(`Status → ${newStatus}${draftMsg}`);
      setDetail(null); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally { setBusy(false); }
  };

  const saveConfig = async () => {
    setBusy(true);
    try {
      const r = await api.put('/admin/listings/config', {
        fee_details_text: cfgDraft.fee_details_text,
        contact_email: cfgDraft.contact_email,
        terms_text: cfgDraft.terms_text,
        enabled: !!cfgDraft.enabled,
      });
      setCfg(r.data.config); setCfgDraft(r.data.config);
      toast.success('Listing config saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="admin-listings-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="inline-flex border border-[#2B3139] p-0.5">
          {[['applications', 'Applications'], ['settings', 'Settings']].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)}
              className={`px-3 py-1 text-[10px] uppercase font-mono tracking-wider ${tab === k ? 'bg-[#FCD535] text-[#0B0E11]' : 'text-[#B7BDC6] hover:text-white'}`}
              data-testid={`listings-admin-tab-${k}`}
            >{l}</button>
          ))}
        </div>
        {tab === 'applications' && (
          <Button onClick={load} className="bg-[#2B3139] hover:bg-[#3A424C] text-white rounded-sm h-8 text-xs" data-testid="listings-admin-refresh">
            <RefreshCw className="w-3 h-3 mr-1.5" /> Refresh
          </Button>
        )}
      </div>

      {tab === 'applications' && (
        <>
          {/* Status filter chips + counts */}
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => setStatusFilter('')}
              className={`px-3 py-1.5 text-[10px] uppercase font-mono tracking-wider border ${statusFilter === '' ? 'bg-[#FCD535] border-[#FCD535] text-[#0B0E11]' : 'bg-[#181A20] border-[#2B3139] text-[#B7BDC6] hover:text-white'}`}
              data-testid="listings-filter-all"
            >All · {rows.length}</button>
            {STATUS_ORDER.map(s => {
              const m = STATUS_META[s]; const active = statusFilter === s;
              return (
                <button key={s} onClick={() => setStatusFilter(s)}
                  className={`px-3 py-1.5 text-[10px] uppercase font-mono tracking-wider border ${active ? 'bg-[#FCD535] border-[#FCD535] text-[#0B0E11]' : `bg-[#181A20] border-[#2B3139] ${m.color} hover:bg-[#2B3139]`}`}
                  data-testid={`listings-filter-${s}`}
                >
                  {m.label} · {counts[s] ?? 0}
                </button>
              );
            })}
          </div>

          <div className="border border-[#2B3139] bg-[#181A20]">
            <div className="hidden md:grid grid-cols-12 px-4 py-2 text-[10px] uppercase tracking-wider text-[#848E9C] border-b border-[#2B3139]">
              <div className="col-span-3">Applicant / Token</div>
              <div className="col-span-1">Symbol</div>
              <div className="col-span-1">Category</div>
              <div className="col-span-2">Contact</div>
              <div className="col-span-2">Submitted</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-1 text-right">Action</div>
            </div>
            {rows.length === 0 ? (
              <div className="p-8 text-center text-[#848E9C] text-sm">No applications.</div>
            ) : rows.map(r => {
              const meta = STATUS_META[r.status] || STATUS_META.pending; const Icon = meta.icon;
              return (
                <div key={r.application_id} className="grid grid-cols-2 md:grid-cols-12 gap-2 px-4 py-3 border-b border-[#2B3139]/40 items-center text-xs" data-testid={`listing-row-${r.application_id}`}>
                  <div className="md:col-span-3 text-white truncate">
                    <div className="flex items-center gap-2">
                      {r.logo_url ? (
                        <img src={r.logo_url} alt="" className="w-6 h-6 rounded-sm object-cover border border-[#2B3139]" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                      ) : (
                        <div className="w-6 h-6 rounded-sm border border-[#2B3139] bg-[#0B0E11] grid place-items-center text-[9px] text-[#FCD535] font-bold">{r.token_symbol?.[0]}</div>
                      )}
                      <div className="truncate">
                        <div className="font-semibold truncate">{r.token_name}</div>
                        <div className="text-[9px] text-[#848E9C] font-mono truncate">{r.applicant_email}</div>
                      </div>
                    </div>
                  </div>
                  <div className="md:col-span-1 font-mono text-[#FCD535]">{r.token_symbol}</div>
                  <div className="md:col-span-1 capitalize text-[#B7BDC6]">{r.category}</div>
                  <div className="md:col-span-2 text-[#B7BDC6] truncate">{r.contact_email}</div>
                  <div className="md:col-span-2 text-[#B7BDC6]">{new Date(r.created_at).toLocaleDateString()}</div>
                  <div className="md:col-span-2">
                    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-wider ${meta.bg} ${meta.color}`}>
                      <Icon className="w-3 h-3" /> {meta.label}
                    </span>
                    {r.launchpad_draft_id && (
                      <div className="text-[9px] text-[#0ECB81] font-mono mt-1 flex items-center gap-1"><Rocket className="w-2.5 h-2.5" />{r.launchpad_draft_id}</div>
                    )}
                  </div>
                  <div className="md:col-span-1 text-right">
                    <Button onClick={() => openDetail(r)} size="sm" className="bg-[#2B3139] hover:bg-[#3A424C] text-[#FCD535] rounded-sm h-6 text-[10px] px-2" data-testid={`listing-review-${r.application_id}`}>
                      Review
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {tab === 'settings' && cfgDraft && (
        <div className="space-y-4">
          <div className="border border-[#2B3139] bg-[#181A20] p-5" data-testid="listings-admin-settings">
            <div className="flex items-center gap-2 mb-4">
              <Settings className="w-4 h-4 text-[#FCD535]" />
              <div className="text-white font-semibold">Public listing page settings</div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#848E9C]">Applications enabled</label>
                <div className="mt-1">
                  <button
                    onClick={() => setCfgDraft(d => ({ ...d, enabled: !d.enabled }))}
                    className={`px-3 h-9 text-xs font-mono uppercase tracking-wider border ${cfgDraft.enabled ? 'bg-[#0ECB81]/10 border-[#0ECB81]/40 text-[#0ECB81]' : 'bg-[#F6465D]/10 border-[#F6465D]/40 text-[#F6465D]'}`}
                    data-testid="listings-settings-toggle-enabled"
                  >
                    {cfgDraft.enabled ? 'Accepting applications' : 'Applications closed'}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#848E9C]">Listing Fees text (shown on /list-your-coin)</label>
                <textarea value={cfgDraft.fee_details_text} onChange={e => setCfgDraft(d => ({ ...d, fee_details_text: e.target.value.slice(0, 5000) }))}
                  rows={5} placeholder="Explain your pricing policy…"
                  data-testid="listings-settings-fee-text"
                  className="mt-1 w-full bg-[#0B0E11] border border-[#2B3139] rounded-sm text-white text-sm p-3 focus:outline-none focus:border-[#FCD535] resize-none" />
                <div className="text-[10px] text-[#848E9C] mt-1 text-right">{cfgDraft.fee_details_text?.length || 0}/5000</div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#848E9C]">Terms text</label>
                <textarea value={cfgDraft.terms_text} onChange={e => setCfgDraft(d => ({ ...d, terms_text: e.target.value.slice(0, 5000) }))}
                  rows={4} data-testid="listings-settings-terms-text"
                  className="mt-1 w-full bg-[#0B0E11] border border-[#2B3139] rounded-sm text-white text-sm p-3 focus:outline-none focus:border-[#FCD535] resize-none" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#848E9C]">Contact email</label>
                <Input value={cfgDraft.contact_email} onChange={e => setCfgDraft(d => ({ ...d, contact_email: e.target.value }))}
                  data-testid="listings-settings-contact"
                  className="mt-1 bg-[#0B0E11] border-[#2B3139] rounded-sm text-white focus-visible:border-[#FCD535]" />
              </div>
              <Button onClick={saveConfig} disabled={busy}
                className="bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] font-semibold rounded-sm"
                data-testid="listings-settings-save"
              >{busy ? 'Saving…' : 'Save settings'}</Button>
            </div>
          </div>
        </div>
      )}

      {/* Review dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-[#181A20] border-[#2B3139] text-white rounded-sm max-w-3xl max-h-[85vh] overflow-y-auto">
          {detail && (() => {
            const meta = STATUS_META[detail.status] || STATUS_META.pending; const Icon = meta.icon;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2 flex-wrap">
                    <span className="text-[#FCD535] font-mono">{detail.token_symbol}</span> · {detail.token_name}
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] uppercase tracking-wider ${meta.bg} ${meta.color}`}>
                      <Icon className="w-3 h-3" /> {meta.label}
                    </span>
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 text-sm">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-[#0B0E11] border border-[#2B3139] p-3">
                      <div className="text-[10px] uppercase text-[#848E9C]">Applicant</div>
                      <div className="text-white text-xs mt-0.5">{detail.applicant_email}</div>
                      <div className="text-[10px] text-[#848E9C] font-mono">{detail.applicant_user_id}</div>
                    </div>
                    <div className="bg-[#0B0E11] border border-[#2B3139] p-3">
                      <div className="text-[10px] uppercase text-[#848E9C]">Category / Contact</div>
                      <div className="text-white text-xs mt-0.5 capitalize">{detail.category}</div>
                      <div className="text-[10px] text-[#B7BDC6]">{detail.contact_email}</div>
                    </div>
                  </div>

                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#848E9C] mb-1">Description</div>
                    <div className="text-[#B7BDC6] whitespace-pre-line max-h-40 overflow-y-auto border border-[#2B3139] p-3 bg-[#0B0E11]">{detail.description}</div>
                  </div>

                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#848E9C] mb-1">Reason for listing</div>
                    <div className="text-[#B7BDC6] whitespace-pre-line max-h-32 overflow-y-auto border border-[#2B3139] p-3 bg-[#0B0E11]">{detail.reason}</div>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    {[['Total Supply', detail.total_supply],
                      ['Circulating', detail.circulating_supply],
                      ['Initial Price', detail.initial_price_usd ? `$${detail.initial_price_usd}` : '']].map(([label, v]) => (
                      <div key={label} className="bg-[#0B0E11] border border-[#2B3139] p-2">
                        <div className="text-[10px] uppercase text-[#848E9C]">{label}</div>
                        <div className="font-mono text-white text-xs mt-0.5">{v || '—'}</div>
                      </div>
                    ))}
                  </div>

                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#848E9C] mb-1">Links</div>
                    <div className="flex flex-wrap gap-2">
                      {[['Website', detail.website], ['Whitepaper', detail.whitepaper_url],
                        ['Twitter', detail.twitter], ['Telegram', detail.telegram],
                        ['Discord', detail.discord], ['GitHub', detail.github],
                        ['Logo', detail.logo_url]].filter(([, v]) => v).map(([k, v]) => (
                        <a key={k} href={v} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[10px] text-[#FCD535] hover:underline bg-[#0B0E11] border border-[#2B3139] px-2 py-1">
                          {k} <ExternalLink className="w-2.5 h-2.5" />
                        </a>
                      ))}
                    </div>
                  </div>

                  <div className="pt-3 border-t border-[#2B3139] space-y-3">
                    <div className="text-white text-sm font-semibold">Review decision</div>
                    <div className="grid grid-cols-5 gap-2">
                      {STATUS_ORDER.map(s => {
                        const m = STATUS_META[s]; const IconS = m.icon;
                        return (
                          <button key={s} onClick={() => setNewStatus(s)}
                            className={`px-2 py-2 text-[10px] uppercase font-mono tracking-wider border flex flex-col items-center gap-1 ${newStatus === s ? `${m.bg} border-[#FCD535] ${m.color}` : `bg-[#0B0E11] border-[#2B3139] ${m.color} hover:border-[#3A424C]`}`}
                            data-testid={`review-set-${s}`}
                          >
                            <IconS className="w-3.5 h-3.5" />
                            {m.label}
                          </button>
                        );
                      })}
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-wider text-[#848E9C]">Admin note (visible to applicant)</label>
                      <textarea value={notes} onChange={e => setNotes(e.target.value.slice(0, 4000))}
                        rows={3} placeholder="e.g. Please provide audit report. Or: Approved — will list next week."
                        data-testid="review-notes"
                        className="mt-1 w-full bg-[#0B0E11] border border-[#2B3139] rounded-sm text-white text-sm p-2 focus:outline-none focus:border-[#FCD535] resize-none" />
                      <div className="text-[10px] text-[#848E9C] mt-1 text-right">{notes.length}/4000</div>
                    </div>
                    {newStatus === 'approved' && ['launchpad', 'both'].includes(detail.category) && !detail.launchpad_draft_id && (
                      <div className="bg-[#0ECB81]/5 border-l-2 border-[#0ECB81] px-3 py-2 text-xs text-[#B7BDC6]">
                        <div className="text-[#0ECB81] uppercase tracking-wider text-[9px] font-mono mb-0.5">Auto-action</div>
                        Approving will automatically create a launchpad campaign draft with these tokenomics. You can finalize dates/tokenomics in the Launchpad admin.
                      </div>
                    )}
                  </div>
                  <div className="text-[10px] text-[#848E9C] font-mono">ID {detail.application_id} · Submitted {new Date(detail.created_at).toLocaleString()}</div>
                </div>
                <DialogFooter>
                  <Button onClick={() => setDetail(null)} className="bg-transparent border border-[#2B3139] hover:bg-[#2B3139] text-white rounded-sm">Cancel</Button>
                  <Button onClick={updateStatus} disabled={busy || newStatus === detail.status}
                    className="bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] rounded-sm font-semibold"
                    data-testid="review-save"
                  >{busy ? 'Saving…' : 'Save decision'}</Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
